import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Portal, Text, Tooltip } from '@chakra-ui/react';
import DOMPurify from 'isomorphic-dompurify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaAlignCenter } from '@react-icons/all-files/fa/FaAlignCenter';
import { FaAlignJustify } from '@react-icons/all-files/fa/FaAlignJustify';
import { FaAlignLeft } from '@react-icons/all-files/fa/FaAlignLeft';
import { FaAlignRight } from '@react-icons/all-files/fa/FaAlignRight';
import { FaBold } from '@react-icons/all-files/fa/FaBold';
import { FaItalic } from '@react-icons/all-files/fa/FaItalic';
import { FaUnderline } from '@react-icons/all-files/fa/FaUnderline';
import { FaLink } from '@react-icons/all-files/fa/FaLink';
import { FaUnlink } from '@react-icons/all-files/fa/FaUnlink';
import { FaFileUpload } from '@react-icons/all-files/fa/FaFileUpload';
import { useDebouncedCallback } from 'use-debounce';
import { useTunes } from '../../components/TuneSelectorButton/components/TuneSelector/useTunes';
import { TuneSelectorButton } from '../../components/TuneSelectorButton/TuneSelectorButton';
import { connectField } from '../../connectors';
import { useNotificationsContext } from '../../contexts/notifications';
import { HighlightTooltip } from '../components/HighlightTooltip/HighlightTooltip';
import { uploadFile } from '../../utils/open-storage-gateway';
const cleanRichText = (text, options) => {
    const { isRTF } = options;
    if (isRTF) {
        return DOMPurify.sanitize(text, {
            ADD_TAGS: ['a'],
            ADD_ATTR: ['href', 'target']
        });
    }
    return DOMPurify.sanitize(text, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: []
    });
};
const CONTAINS_ANCHOR = /<a[\s/>]/i;
const ANCHOR_NAME_ATTRIBUTES = ['aria-label', 'aria-labelledby', 'title'];
const stripEmptyAnchors = (html) => {
    if (!CONTAINS_ANCHOR.test(html)) {
        return html;
    }
    const root = DOMPurify.sanitize(html, {
        ADD_TAGS: ['a'],
        ADD_ATTR: ['href', 'target'],
        RETURN_DOM: true
    });
    root.querySelectorAll('a').forEach(anchor => {
        const hasName = ANCHOR_NAME_ATTRIBUTES.some(attribute => (anchor.getAttribute(attribute) || '').trim() !== '');
        if (anchor.hasAttribute('href') &&
            (anchor.textContent || '').trim() === '' &&
            anchor.children.length === 0 &&
            !hasName) {
            anchor.remove();
        }
    });
    return root.innerHTML;
};
const PHRASING_CONTENT = /^(a|abbr|b|bdi|bdo|br|cite|code|data|dfn|em|i|kbd|mark|q|rp|rt|ruby|s|samp|small|span|strong|sub|sup|time|u|var|wbr)$/i;
const containsFlowContent = (input) => {
    const tags = input.match(/<\s*\/?\s*([a-zA-Z][a-zA-Z0-9-]*)/g);
    if (!tags)
        return false;
    return tags.some(tag => !PHRASING_CONTENT.test(tag.replace(/^<\s*\/?\s*/, '')));
};
export const TextField = connectField(({ jaenField, defaultValue, as: Wrapper = Text, asAs: definedAsAs, styleTunes: fieldStyleTunes = [], isRTF = true, ...rest }) => {
    const [value, setValue] = useState(() => {
        return cleanRichText(jaenField.staticValue || defaultValue || '', {
            isRTF
        });
    });
    useEffect(() => {
        const newValue = cleanRichText(jaenField.value || jaenField.staticValue || defaultValue || '', {
            isRTF
        });
        setValue(newValue);
    }, [jaenField.value, isRTF]);
    const displayValue = useMemo(() => {
        if (jaenField.isEditing || !isRTF) {
            return value;
        }
        return stripEmptyAnchors(value);
    }, [value, jaenField.isEditing, isRTF]);
    const { toast } = useNotificationsContext();
    const asAs = useMemo(() => {
        if (containsFlowContent(value)) {
            return 'div';
        }
        if (definedAsAs) {
            return definedAsAs;
        }
        return Wrapper.displayName === 'Heading' ? 'h2' : undefined;
    }, [value, Wrapper.displayName, definedAsAs]);
    const handleTextSave = useDebouncedCallback(useCallback((data) => {
        if (data === value) {
            return;
        }
        jaenField.onUpdateValue(data || undefined);
        toast({
            title: 'Text saved',
            description: 'The text has been saved',
            status: 'info'
        });
    }, [value]), 500);
    useEffect(() => {
        if (jaenField.isEditing) {
            const as = typeof Wrapper === 'string'
                ? Wrapper
                : typeof asAs === 'string'
                    ? asAs
                    : undefined;
            jaenField.register({
                as
            });
        }
    }, [jaenField.isEditing]);
    const handleContentBlur = useCallback(evt => {
        handleTextSave(evt.currentTarget.innerHTML);
    }, []);
    const handleFileChange = async (event) => {
        const fileInput = document.getElementById('page-file-upload-input');
        const file = event.target.files?.[0];
        if (file) {
            const randomId = Math.random().toString(36).substring(7);
            document.execCommand('insertHTML', false, `<a id="${randomId}" download="${file.name}" href="#" target="_blank">${file.name}</a>`);
            const { fileUrl } = await uploadFile(file);
            const link = document.getElementById(randomId);
            if (link) {
                link.setAttribute('href', fileUrl);
                link.removeAttribute('id');
            }
            else {
                throw new Error('Could not find link element');
            }
        }
        if (fileInput) {
            fileInput.value = '';
            fileInput.removeEventListener('change', handleFileChange);
        }
    };
    const alignmentTune = {
        type: 'groupTune',
        name: 'alignment',
        label: 'Alignment',
        tunes: [
            {
                name: 'left',
                Icon: FaAlignLeft,
                props: {
                    textAlign: 'left'
                }
            },
            {
                name: 'center',
                Icon: FaAlignCenter,
                props: {
                    textAlign: 'center'
                }
            },
            {
                name: 'right',
                Icon: FaAlignRight,
                props: {
                    textAlign: 'right'
                }
            },
            {
                name: 'justify',
                Icon: FaAlignJustify,
                props: {
                    textAlign: 'justify'
                }
            }
        ]
    };
    const styleTune = {
        type: 'groupTune',
        name: 'style',
        label: 'Style',
        tunes: [
            {
                name: 'bold',
                Icon: FaBold,
                onTune: () => {
                    document.execCommand('bold');
                }
            },
            {
                name: 'italic',
                Icon: FaItalic,
                onTune: () => {
                    document.execCommand('italic');
                }
            },
            {
                name: 'underline',
                Icon: FaUnderline,
                onTune: () => {
                    document.execCommand('underline');
                }
            },
            {
                name: 'link',
                Icon: FaLink,
                onTune: async () => {
                    const url = window.prompt('Enter the URL');
                    if (url) {
                        const selection = document.getSelection();
                        document.execCommand('insertHTML', false, `<a href="${url}" target="_blank">${selection}</a>`);
                    }
                }
            },
            {
                name: 'unlink',
                Icon: FaUnlink,
                onTune: () => {
                    document.execCommand('unlink', false);
                }
            },
            {
                name: 'file',
                Icon: FaFileUpload,
                onTune: async () => {
                    const fileInput = document.getElementById('page-file-upload-input');
                    if (fileInput) {
                        fileInput.addEventListener('change', handleFileChange);
                        fileInput.click();
                    }
                }
            }
        ]
    };
    const tunes = useTunes({
        props: { ...rest, asAs },
        activeTunes: jaenField.activeTunes,
        tunes: [
            alignmentTune,
            ...(isRTF ? [styleTune, ...fieldStyleTunes] : []),
            ...jaenField.tunes
        ]
    });
    return (_jsx(HighlightTooltip, { id: jaenField.id || jaenField.name, actions: [
            _jsx(Button, { variant: 'field-highlighter-tooltip-text', children: _jsxs(Tooltip.Root, { openDelay: 0, closeDelay: 0, positioning: { placement: 'top-start' }, children: [_jsx(Tooltip.Trigger, { asChild: true, children: _jsx(Text, { children: "Text" }) }), _jsx(Portal, { children: _jsx(Tooltip.Positioner, { children: _jsx(Tooltip.Content, { children: `ID: ${jaenField.id}` }) }) })] }) }, `jaen-highlight-tooltip-text-${jaenField.name}`),
            ...(isRTF
                ? [
                    _jsx(TuneSelectorButton, { "aria-label": "Customize", tunes: [styleTune, ...fieldStyleTunes], activeTunes: tunes.activeTunes, onTune: jaenField.tune, children: _jsx(Text, { as: "span", fontSize: "sm", fontFamily: "serif", children: "T" }) }, `jaen-highlight-tooltip-tune-${jaenField.name}`)
                ]
                : []),
            _jsx(TuneSelectorButton, { "aria-label": "Customize", tunes: [alignmentTune, ...jaenField.tunes], activeTunes: tunes.activeTunes, onTune: jaenField.tune }, `jaen-highlight-tooltip-tune-${jaenField.name}`)
        ], isEditing: jaenField.isEditing, as: Wrapper, asAs: asAs, minW: "1rem", className: jaenField.className, style: {
            ...jaenField.style,
            ...rest.style
        }, ...rest, ...tunes.activeProps, asProps: {
            outline: 'none',
            dangerouslySetInnerHTML: { __html: displayValue },
            contentEditable: jaenField.isEditing,
            onBlur: handleContentBlur,
            onPaste: (evt) => {
                evt.preventDefault();
                if (isRTF) {
                    let text = evt.clipboardData.getData('text/html') ||
                        evt.clipboardData.getData('text');
                    text = DOMPurify.sanitize(text, {
                        ALLOWED_TAGS: ['br', 'span'],
                        ALLOWED_ATTR: []
                    });
                    document.execCommand('insertHTML', false, text);
                }
                else {
                    const text = evt.clipboardData.getData('text');
                    document.execCommand('insertText', false, text);
                }
            }
        }, css: {
            '& a': {
                color: 'brand.500',
                textDecoration: 'underline',
                _dark: {
                    color: 'brand.300'
                }
            }
        } }));
}, {
    fieldType: 'IMA:TextField'
});
